/**
 * Login page component.
 *
 * Displays sign-in options using Microsoft authentication.
 * When in Teams, auto-authenticates via SSO and redirects.
 */

import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export function Login() {
  const { isAuthenticated, isTeams, login, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Determine redirect target
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'
  
  console.log('[Login] Render:', { isAuthenticated, isTeams, isLoading, from })

  // If already authenticated, redirect immediately
  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  // Show loading while auth state is being determined
  // This is important for Teams SSO - wait for token to be acquired
  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">
            {isTeams ? 'Kirjaudutaan Teamsilla...' : 'Ladataan...'}
          </p>
        </div>
      </div>
    )
  }

  // If in Teams but not authenticated after loading, something went wrong
  // Still show login button as fallback
  const handleLogin = async () => {
    try {
      await login()
      // After successful login, redirect
      navigate(from, { replace: true })
    } catch (error) {
      console.error('Login error:', error)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Tervetuloa!</CardTitle>
          <CardDescription>
            {isTeams 
              ? 'Kirjaudu sisään Teams-tililläsi napsauttamalla alla olevaa painiketta.'
              : 'Kirjaudu sisään Microsoft-tililläsi käyttääksesi tehtävienhallintajärjestelmää.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <Button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full max-w-xs"
            size="lg"
          >
            <span className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 21 21"
                className="h-5 w-5"
              >
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              {isTeams ? 'Kirjaudu sisään Teamsilla' : 'Kirjaudu sisään Microsoftilla'}
            </span>
          </Button>
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            Kirjautumalla sisään hyväksyt käyttöehdot ja tietosuojakäytännön.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default Login

